import { EtcOutboundSearchParams } from '@/services/outbound/outboundEtc/types'

/**
 * 메뉴 세션·URL 쿼리에 싣는 조회 조건 모양. (MOMS 공통 UX — 다른 메뉴 다녀와도 마지막 조회 조건이 남는다)
 *
 * 탭 전환은 react-router 언마운트라 로컬 state 가 통째로 날아간다. 조건을 컴포넌트 밖(URL)에 두고
 * 마운트 때 되살린다. 저장은 조회 버튼에서만 하고, 복원은 마운트 1회다.
 *
 * 복원 통로는 `core.FilterArea` 가 `onFormReady` 로 넘기는 antd Form 인스턴스다. 개별 필터
 * 컴포넌트는 값을 받을 prop 이 없지만, Form.Item 으로 등록된 필드는 `setFieldsValue` 로 채워진다.
 * 그래서 저장 대상은 필터 필드 전부다.
 *
 * 목록 검색어(`keyword`)와 페이지 번호(`page`)도 함께 싣는다 — 조건만 남고 보던 페이지가 1 로
 * 돌아가면 사용자에게는 여전히 새로고침으로 보인다. 이 화면은 서버 페이지네이션이 있어 해당된다.
 */
export interface EtcOutboundSearchQuery {
  SPR_NM?: string
  LOGISTICS_SERVICE_TYPES?: string[]
  ORD_NO?: string[]
  ORD_OPT_NO?: string[]
  TRS_TYPE?: string
  keyword?: string
  page?: string
}

export interface EtcOutboundRestored {
  params: EtcOutboundSearchParams
  keyword: string
  page: number
}

/** 세션·URL 에 저장할 형태로 만든다. 빈 값은 키 자체를 빼 쿼리스트링을 짧게 유지한다. */
export const toSearchQuery = ({ params, keyword, page }: EtcOutboundRestored): EtcOutboundSearchQuery => ({
  ...(params.SPR_NM ? { SPR_NM: params.SPR_NM } : {}),
  ...(params.LOGISTICS_SERVICE_TYPES?.length ? { LOGISTICS_SERVICE_TYPES: params.LOGISTICS_SERVICE_TYPES } : {}),
  ...(params.ORD_NO.length ? { ORD_NO: params.ORD_NO } : {}),
  ...(params.ORD_OPT_NO.length ? { ORD_OPT_NO: params.ORD_OPT_NO } : {}),
  ...(params.TRS_TYPE ? { TRS_TYPE: params.TRS_TYPE } : {}),
  ...(keyword ? { keyword } : {}),
  ...(page > 1 ? { page: String(page) } : {}),
})

/** 조작된 URL 이 화면을 깨뜨리지 않게 문자열만 받는다. */
const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined)

/**
 * 단건도 배열로 받는다 — `qs` 는 `ORD_NO=A` 를 문자열로, `ORD_NO=A,B` 를 배열로 돌려준다.
 * 두 모양이 같은 필드에서 나오므로 여기서 배열로 통일한다.
 */
const list = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean)
  const one = text(value)
  return one ? [one] : []
}

/** 1 미만·정수 아님은 1 페이지로 본다. */
const pageNumber = (value: unknown): number => {
  const parsed = Number(text(value))
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1
}

/** 저장된 쿼리를 화면 상태로 되돌린다. 없는 항목은 빈 조건이 된다. */
export const fromSearchQuery = (query: Partial<EtcOutboundSearchQuery>): EtcOutboundRestored => {
  const serviceTypes = list(query.LOGISTICS_SERVICE_TYPES)

  return {
    params: {
      SPR_NM: text(query.SPR_NM),
      LOGISTICS_SERVICE_TYPES: serviceTypes.length ? serviceTypes : undefined,
      ORD_NO: list(query.ORD_NO),
      ORD_OPT_NO: list(query.ORD_OPT_NO),
      TRS_TYPE: text(query.TRS_TYPE),
    },
    keyword: text(query.keyword) ?? '',
    page: pageNumber(query.page),
  }
}

/**
 * 복원된 조건으로 마운트 시 조회를 돌려야 하는지.
 *
 * 화주코드가 조회 필수값이라 그것만 본다 — BE `EtcOutboundValidator.validateSearch` 가
 * `requireNotNull` 로 보고 화면도 없으면 알럿으로 막는다. 최초 진입(복원할 조건 없음)에는
 * 조회하지 않는다.
 */
export const hasRestoredCondition = (restored: EtcOutboundRestored) => Boolean(restored.params.SPR_NM)
